import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { GoogleButton } from "./Login";
import { Loader2 } from "lucide-react";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1767300258298-21f93cbe723f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODY1MTU0OTl8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const navigate = useNavigate();
  const { register, t } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register({ name: form.name, email: form.email, password: form.password });
      navigate(u.role === "admin" ? "/admin" : u.role === "trainer" ? "/trainer" : "/test");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo onClick={() => navigate("/")} /></div>
          <h1 className="font-heading text-3xl mb-2" style={{ color: "var(--text)" }}>{t("register_title")}</h1>
          <p className="text-sm mb-8" style={{ color: "var(--text-dim)" }}>{t("register_sub")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("label_name")}</label>
              <input data-testid="register-name" required value={form.name} onChange={upd("name")} className="input-amt w-full px-4 py-3 mt-1" placeholder="Jane Doe" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>Email</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={upd("email")} className="input-amt w-full px-4 py-3 mt-1" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("password")}</label>
              <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="input-amt w-full px-4 py-3 mt-1" placeholder="••••••••" />
            </div>
            <p className="text-xs" style={{ color: "var(--text-dim)" }} data-testid="trainer-domain-note">
              {t("trainer_domain_note")}
            </p>
            {error && <p data-testid="register-error" className="text-sm" style={{ color: "#EF4444" }}>{error}</p>}
            <button data-testid="register-submit" disabled={loading} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
              {loading && <Loader2 className="animate-spin" size={18} />} {t("nav_register")}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--text-dim)" }}>or</span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>
          <GoogleButton label={t("google_login")} />

          <p className="text-sm mt-8 text-center" style={{ color: "var(--text-dim)" }}>
            {t("have_account")}{" "}
            <Link to="/login" data-testid="go-login" className="gold-text font-medium">{t("nav_login")}</Link>
          </p>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={AUTH_IMG} alt="office" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(14,31,51,0.7)" }} />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between items-end text-right">
          <Logo variant="full" size="lg" onClick={() => navigate("/")} />
          <div>
            <h2 className="font-heading text-3xl leading-tight" style={{ color: "var(--text)" }}>
              Your journey to<br />self-awareness starts here.
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
